import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import {
  IconShieldCheck,
  IconCheck,
  IconBuilding,
  IconMail,
  IconPlus,
  IconTrash,
  IconEdit,
  IconDroplet,
  IconX,
  IconLock,
  IconArrowRight,
  IconCamera,
} from '@tabler/icons-react';

const STEPS = [
  { num: 1, label: 'Your details' },
  { num: 2, label: 'Add branches' },
  { num: 3, label: 'Invite team' },
  { num: 4, label: 'Connect Outlook' },
  { num: 5, label: 'Choose plan' },
];

const DEPARTMENTS = ['Workshop', 'Sales', 'Admin'];

export default function SeniorManagerOnboarding() {
  const { userProfile, orgId, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [firstName, setFirstName] = useState(userProfile?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(userProfile?.name?.split(' ').slice(1).join(' ') || '');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [photoURL, setPhotoURL] = useState(userProfile?.photoURL || null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Step 2
  const [branches, setBranches] = useState([]);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchName, setBranchName] = useState('');
  const [branchAddress, setBranchAddress] = useState('');
  const [branchDepts, setBranchDepts] = useState([]);

  // Step 3
  const [fuelManager, setFuelManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteType, setInviteType] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBranch, setInviteBranch] = useState('');
  const [inviteDept, setInviteDept] = useState('');

  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${orgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      setPhotoURL(url);
    } catch (err) {
      console.error('Photo upload error:', err);
      throw err;
    }
  };

  const handleSaveBranch = () => {
    if (!branchName) return;
    if (editingBranch !== null) {
      setBranches(prev => prev.map((b, i) => i === editingBranch
        ? { name: branchName, address: branchAddress, departments: branchDepts }
        : b
      ));
    } else {
      setBranches(prev => [...prev, { name: branchName, address: branchAddress, departments: branchDepts }]);
    }
    setShowBranchModal(false);
    setBranchName('');
    setBranchAddress('');
    setBranchDepts([]);
    setEditingBranch(null);
  };

  const openEditBranch = (i) => {
    setEditingBranch(i);
    setBranchName(branches[i].name);
    setBranchAddress(branches[i].address);
    setBranchDepts(branches[i].departments);
    setShowBranchModal(true);
  };

  const toggleDept = (dept) => {
    setBranchDepts(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  };

  const openInvite = (type) => {
    setInviteType(type);
    setInviteEmail('');
    setInviteBranch('');
    setInviteDept('');
    setShowInviteModal(true);
  };

  const handleInvite = () => {
    if (!inviteEmail) return;
    if (inviteType === 'fuel') {
      setFuelManager({ email: inviteEmail });
    } else {
      setManagers(prev => [...prev, {
        email: inviteEmail,
        branch: inviteBranch,
        department: inviteDept,
      }]);
    }
    setShowInviteModal(false);
  };

  const handleFinish = async () => {
    try {
      for (const branch of branches) {
        const slug = branch.name.toLowerCase().replace(/\s+/g, '-');
        await setDoc(doc(db, 'organisations', orgId, 'branches', slug), {
          name: branch.name,
          address: branch.address,
          departments: branch.departments,
          createdAt: serverTimestamp(),
        });
      }
      await setDoc(doc(db, 'organisations', orgId, 'users', user.uid), {
        name: `${firstName} ${lastName}`.trim(),
        jobTitle,
        phone,
        photoURL: photoURL || null,
        onboardingComplete: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      navigate('/group');
    } catch (err) {
      console.error(err);
    }
  };

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', fontFamily: 'inherit', overflow: 'hidden' }}>

      {/* Left panel */}
      <div style={{ width: '28%', background: 'var(--color-brand)', padding: '40px 32px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconShieldCheck size={18} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 400, color: '#fff' }}>Veritas</div>
        </div>

        <div style={{ flex: 1 }}>
          {STEPS.map((s, i) => {
            const isDone = step > s.num;
            const isActive = step === s.num;
            return (
              <div key={s.num}>
                <div style={{ display: 'flex', gap: 12, paddingBottom: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: isDone ? '#1D9E75' : isActive ? '#fff' : 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: isActive ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                      {isDone ? <IconCheck size={13} stroke={2} color="white" /> : s.num}
                    </div>
                    {i < STEPS.length - 1 && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.2)', marginTop: 4 }} />}
                  </div>
                  <div style={{ flex: 1, paddingTop: 3 }}>
                    <div style={{ fontSize: 13, color: isDone ? 'rgba(255,255,255,0.6)' : isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                      {s.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, background: 'var(--color-bg-tertiary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '48px 64px' }}>

          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.05em', marginBottom: 8 }}>
            STEP {step} OF {STEPS.length}
          </div>

          {/* ── Step 1 — Your details ── */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Welcome, {firstName || 'there'}</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 36, lineHeight: 1.6 }}>
                Let's set up your profile before we get your branches and team added.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 48, alignItems: 'start', marginBottom: 36 }}>

                {/* Avatar with crop */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div
                    onClick={() => setShowCropModal(true)}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: photoURL ? 'transparent' : '#FDF0E8',
                      backgroundImage: photoURL ? `url(${photoURL})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      color: '#993C1D',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '2px solid transparent',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    {!photoURL && (initials || '?')}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s',
                      borderRadius: '50%',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '1'); }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '0'); }}
                    >
                      <IconCamera size={22} color="white" stroke={1.5} style={{ opacity: 0, transition: 'opacity 0.2s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span onClick={() => setShowCropModal(true)} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer' }}>
                      {photoURL ? 'Change' : 'Add photo'}
                    </span>
                    {photoURL && (
                      <>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>·</span>
                        <span onClick={() => setPhotoURL(null)} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>Remove</span>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'center', maxWidth: 100 }}>JPG, PNG or WebP</div>
                </div>

                {/* Fields */}
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>First name</label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Last name</label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Job title</label>
                    <input type="text" placeholder="e.g. Fleet Director, Operations Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Phone number</label>
                    <input type="tel" placeholder="+44 7700 900 000" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Work email</label>
                    <input type="email" value={userProfile?.email || ''} readOnly style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }} />
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Change at any time in profile settings</div>
                  </div>
                </div>
              </div>

              {/* Org divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>ORGANISATION</span>
                <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Organisation name</label>
                  <input type="text" defaultValue={userProfile?.orgName || ''} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Industry</label>
                  <select style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }}>
                    <option>Agricultural dealership</option>
                    <option>Plant hire</option>
                    <option>Logistics</option>
                    <option>Construction</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2 — Add branches ── */}
          {step === 2 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Add your branches</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
                Add each branch location and assign the departments that operate there.
              </p>

              {branches.length > 0 && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, background: 'var(--color-brand-light)', color: 'var(--color-brand)', borderRadius: 'var(--radius-full)', padding: '3px 10px', marginBottom: 16 }}>
                  <IconBuilding size={12} stroke={1.5} />
                  {branches.length} {branches.length === 1 ? 'branch' : 'branches'} added
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {branches.map((b, i) => (
                  <div key={i} style={{ width: 150, height: 150, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 14px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                      <IconBuilding size={18} color="var(--color-brand)" stroke={1.5} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, flex: 1 }}>
                      {b.departments.map(d => (
                        <span key={d} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)', border: '0.5px solid var(--color-border-light)' }}>{d}</span>
                      ))}
                    </div>
                    <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <span onClick={() => openEditBranch(i)} style={{ fontSize: 11, color: 'var(--color-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <IconEdit size={11} stroke={1.5} /> Edit
                      </span>
                    </div>
                  </div>
                ))}

                <div
                  onClick={() => { setEditingBranch(null); setBranchName(''); setBranchAddress(''); setBranchDepts([]); setShowBranchModal(true); }}
                  style={{ width: 150, height: 150, border: '1.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', background: 'transparent', transition: 'border-color 0.15s, background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.background = 'var(--color-brand-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconPlus size={18} color="var(--color-text-tertiary)" stroke={1.5} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontWeight: 400 }}>Add branch</span>
                </div>
              </div>
            </>
          )}

          {/* ── Step 3 — Invite team ── */}
          {step === 3 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Invite your team</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
                Add your fuel manager and branch managers. The fuel manager is required before you can go live.
              </p>

              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 400 }}>Fuel manager</span>
                  <span style={{ fontSize: 10, background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-full)', padding: '2px 8px' }}>Required</span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>One person responsible for monitoring Derv levels across all branches.</p>

                {fuelManager ? (
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ width: 150, height: 150, background: '#E1F5EE', border: '2px solid #1D9E75', borderRadius: 'var(--radius-lg)', padding: '16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#085041', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#E1F5EE', marginBottom: 8 }}>
                        {fuelManager.email.slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ fontSize: 11, color: '#085041', textAlign: 'center', marginBottom: 4, wordBreak: 'break-all' }}>{fuelManager.email}</div>
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 20, background: '#085041', color: '#E1F5EE' }}>Fuel manager</span>
                      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                        <span onClick={() => setFuelManager(null)} style={{ fontSize: 11, color: '#085041', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <IconTrash size={11} stroke={1.5} /> Remove
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => openInvite('fuel')} style={{ width: 150, height: 150, border: '1.5px dashed #5DCAA5', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', background: '#E1F5EE' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#085041', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconDroplet size={18} color="white" stroke={1.5} />
                    </div>
                    <span style={{ fontSize: 12, color: '#085041' }}>Add fuel manager</span>
                  </div>
                )}
              </div>

              <div style={{ height: '0.5px', background: 'var(--color-border-light)', marginBottom: 24 }} />

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 400 }}>Branch managers</span>
                  <span style={{ fontSize: 10, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)', borderRadius: 'var(--radius-full)', padding: '2px 8px' }}>Optional</span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>Branch managers are assigned to a specific branch and department.</p>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {managers.map((m, i) => (
                    <div key={i} style={{ width: 150, height: 150, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FDF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#993C1D', marginBottom: 8 }}>
                        {m.email.slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-primary)', textAlign: 'center', marginBottom: 4, wordBreak: 'break-all' }}>{m.email}</div>
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 20, background: '#E6F1FB', color: '#185FA5' }}>Manager</span>
                      {m.branch && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4, textAlign: 'center' }}>{m.branch} · {m.department}</div>}
                      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                        <span onClick={() => setManagers(prev => prev.filter((_, j) => j !== i))} style={{ fontSize: 11, color: 'var(--color-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <IconTrash size={11} stroke={1.5} /> Remove
                        </span>
                      </div>
                    </div>
                  ))}

                  <div onClick={() => openInvite('manager')} style={{ width: 150, height: 150, border: '1.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', background: 'transparent', transition: 'border-color 0.15s, background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.background = 'var(--color-brand-light)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconPlus size={18} color="var(--color-text-tertiary)" stroke={1.5} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Add manager</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 4 — Connect Outlook ── */}
          {step === 4 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Connect Outlook</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 36, lineHeight: 1.6 }}>
                Connect your Microsoft Outlook so Veritas can send automated emails from your inbox.
              </p>

              <div style={{ background: '#E6F1FB', border: '0.5px solid #B5D4F4', borderRadius: 'var(--radius-lg)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, maxWidth: 480 }}>
                <div style={{ width: 48, height: 48, background: '#0078D4', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconMail size={24} color="white" stroke={1.5} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#185FA5', marginBottom: 3 }}>Microsoft Outlook</div>
                  <div style={{ fontSize: 12, color: '#185FA5', opacity: 0.8 }}>Connect your work Outlook account via Microsoft 365</div>
                </div>
              </div>

              <button style={{ background: '#0078D4', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '12px 24px', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <IconMail size={16} stroke={1.5} />
                Connect Microsoft Outlook
              </button>

              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 8 }}>
                You can connect Outlook later from your profile settings.
              </div>
            </>
          )}

          {/* ── Step 5 — Choose plan ── */}
          {step === 5 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Choose your plan</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 32, lineHeight: 1.6 }}>
                Select a plan to activate Veritas for your organisation.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28, maxWidth: 680 }}>
                {[
                  { name: 'Starter', price: 'TBC', desc: '1 branch · up to 10 users', features: ['Daily checks', 'Fuel management', 'Incident logging'], popular: false },
                  { name: 'Professional', price: 'TBC', desc: 'Up to 4 branches · 50 users', features: ['Everything in Starter', 'Crash detection', 'Group overview', 'DVLA integration', 'PDF reports'], popular: true },
                  { name: 'Enterprise', price: 'TBC', desc: 'Unlimited branches & users', features: ['Everything in Pro', 'Dedicated support', 'SLA guarantee', 'Priority onboarding'], popular: false },
                ].map(plan => (
                  <div key={plan.name} style={{ background: '#fff', border: plan.popular ? '2px solid var(--color-brand)' : '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 16px', position: 'relative' }}>
                    {plan.popular && (
                      <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-brand)', color: '#fff', fontSize: 10, padding: '2px 12px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                        Most popular
                      </div>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 4 }}>{plan.name}</div>
                    <div style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{plan.price}<span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>/mo</span></div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 12, paddingBottom: 12, borderBottom: '0.5px solid var(--color-border-light)' }}>{plan.desc}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {plan.features.map(f => (
                        <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          <IconCheck size={12} color="var(--color-success)" stroke={2} /> {f}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 20, maxWidth: 480 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 400 }}>Payment details</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ fontSize: 10, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 3, padding: '2px 6px', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <IconLock size={10} stroke={1.5} /> Secure
                    </div>
                    <div style={{ fontSize: 10, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 3, padding: '2px 6px', color: 'var(--color-text-tertiary)' }}>Powered by Stripe</div>
                  </div>
                </div>
                <div style={{ height: 42, background: '#fff', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', marginBottom: 10, display: 'flex', alignItems: 'center', padding: '0 14px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Card number</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ height: 42, background: '#fff', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', display: 'flex', alignItems: 'center', padding: '0 14px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Expiry</div>
                  <div style={{ height: 42, background: '#fff', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', display: 'flex', alignItems: 'center', padding: '0 14px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>CVC</div>
                </div>
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ borderTop: '0.5px solid var(--color-border-light)', padding: '16px 64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Step {step} of {STEPS.length}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button onClick={handleBack} style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>Back</button>
            )}
            {step < STEPS.length ? (
              <button
                onClick={handleNext}
                disabled={step === 3 && !fuelManager}
                style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: step === 3 && !fuelManager ? 'not-allowed' : 'pointer', opacity: step === 3 && !fuelManager ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                Next <IconArrowRight size={15} stroke={1.5} />
              </button>
            ) : (
              <button onClick={handleFinish} style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconCheck size={15} stroke={1.5} /> Activate Veritas
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Branch modal */}
      {showBranchModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 420, border: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>{editingBranch !== null ? 'Edit branch' : 'Add branch'}</div>
              <div onClick={() => setShowBranchModal(false)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={20} stroke={1.5} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Branch name</label>
              <input type="text" placeholder="Aylesbury" value={branchName} onChange={e => setBranchName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Address</label>
              <input type="text" placeholder="Aylesbury, Buckinghamshire" value={branchAddress} onChange={e => setBranchAddress(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Departments</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {DEPARTMENTS.map(d => (
                  <div key={d} onClick={() => toggleDept(d)} style={{ padding: '6px 14px', borderRadius: 'var(--radius-full)', border: '0.5px solid', borderColor: branchDepts.includes(d) ? 'var(--color-brand)' : 'var(--color-border-medium)', background: branchDepts.includes(d) ? 'var(--color-brand-light)' : 'transparent', color: branchDepts.includes(d) ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
                    {d}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowBranchModal(false)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveBranch} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>
                {editingBranch !== null ? 'Save changes' : 'Add branch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {showInviteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 420, border: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>{inviteType === 'fuel' ? 'Invite fuel manager' : 'Invite branch manager'}</div>
              <div onClick={() => setShowInviteModal(false)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={20} stroke={1.5} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Email address</label>
              <input type="email" placeholder="name@company.co.uk" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {inviteType === 'manager' && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Branch</label>
                  <select value={inviteBranch} onChange={e => setInviteBranch(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                    <option value="">Select branch</option>
                    {branches.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Department</label>
                  <select value={inviteDept} onChange={e => setInviteDept(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                    <option value="">Select department</option>
                    {inviteBranch && branches.find(b => b.name === inviteBranch)?.departments.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowInviteModal(false)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleInvite} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Send invitation</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo crop modal */}
      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}

    </div>
  );
}